import { AvailableLanguages } from '@repo/toolkit/language-types'
import { TFunction } from 'i18next'
import { z, ZodType } from 'zod'

import { GenderEnum } from '../../../../lib/types/enums/common/gender.enum'
import { ContactEnum } from '../../../../lib/types/enums/enrollment/contact.enum'
import { MedicalConditionEnum } from '../../../../lib/types/enums/enrollment/medical-condition-enum'
import { FriendPlaceEnum } from '../../../../lib/types/enums/friend/friend-place.enum'
import { InsuranceTypeEnum } from '../../../../lib/types/enums/insurance/insurance-type.enum'
import { FamilyArrangementEnum } from '../../../../lib/types/enums/ol/family-arrangement.enum'
import { Enrollment } from '../../../../lib/types/models/enrollment/enrollment'
import {
  CreateEnrollmentInput,
  EnrollmentInput,
  EnrollmentQuotationInput
} from '../../../../lib/types/models/enrollment/enrollment-input'
import { EnrollmentProposition } from '../../../../lib/types/models/enrollment-proposition/enrollment-proposition'
import {
  EnrollmentPropositionInput,
  EnrollmentPropositionRequest,
  InsuranceEnrollmentPropositionInput,
  JourneyEnrollmentPropositionInput,
  JourneyPlaceEnrollmentPropositionInput,
  OptionEnrollmentPropositionInput,
  TripEnrollmentPropositionInput,
  TripPlaceEnrollmentPropositionInput
} from '../../../../lib/types/models/enrollment-proposition/enrollment-proposition-input'
import {
  mapOtoIndividualPropositionToInput,
  OtoIndividualPropositionInput
} from '../../../../lib/types/models/enrollment-proposition/oto-individual-proposition-input'
import { FriendInput } from '../../../../lib/types/models/friend/friend-input'
import { InsuranceProperties } from '../../../../lib/types/models/insurance/properties.insurance'
import { JourneyPlaceLight } from '../../../../lib/types/models/journey-place/journey-place'
import { OptionLight } from '../../../../lib/types/models/options/option'
import { JourneyLight } from '../../../../lib/types/models/product/journey'
import { TripLight } from '../../../../lib/types/models/trip/trip'
import { TripPlace } from '../../../../lib/types/models/trip-places/trip-places'
import { createEnumSchema } from '../../../../lib/utils/validations/enum-validation'
import { idOnlySchema } from '../../../../lib/utils/validations/id-only.validation'
import { createNumberSchema } from '../../../../lib/utils/validations/int-array-validation'
import { PHONE_NUMBER_SCHEMA } from '../../../../lib/utils/validations/phone-number.validation'
import { createStringSchema } from '../../../../lib/utils/validations/string-validation'
import {
  ADDRESS_INFORMATION_SCHEMA,
  AddressInformationInput
} from '../../enrollment-profile/shared/address-information-form'
import {
  CHILD_INFORMATION_SCHEMA,
  ChildInformationInput
} from '../../enrollment-profile/shared/child-information-form'
import {
  CONTACT_INFORMATION_SCHEMA,
  ContactInformationInput
} from '../../enrollment-profile/shared/contact-information-form'
import {
  addParentContactPriorityValidation,
  PARENT_INFORMATION_BASE_SCHEMA,
  ParentInformationInput
} from '../../enrollment-profile/shared/parent-information-form'
import { PropositionReductionInput } from '../../enrollment-profile/shared/proposition-reduction-form'

export const FRIEND_SCHEMA = z.object({
  firstName: createStringSchema('firstName'),
  lastName: createStringSchema('lastName'),
  type: createEnumSchema({ enumType: FriendPlaceEnum, fieldName: 'type' })
})

export const INSURANCE_SCHEMA = z.object({
  id: createNumberSchema('id'),
  name: createStringSchema('name'),
  price: createNumberSchema('price', { nullish: true }),
  type: createEnumSchema({ enumType: InsuranceTypeEnum, fieldName: 'type' })
})

export const ENROLLMENT_PROPOSITION_SCHEMA: ZodType<EnrollmentPropositionInput> = z
  .object({
    journey: idOnlySchema<JourneyLight>(createNumberSchema('id', { isInteger: true }), {
      message: 'Le voyage est requis'
    }),
    options: z
      .array(
        idOnlySchema<OptionLight>(createNumberSchema('id', { isInteger: true }), {
          message: "L'option est requise"
        })
      )
      .optional(),
    familyArrangement: createEnumSchema({
      enumType: FamilyArrangementEnum,
      fieldName: 'familyArrangement',
      nullish: true
    }),
    declaredFriends: z.array(FRIEND_SCHEMA).optional(),
    mainTrip: idOnlySchema<TripLight>(createNumberSchema('id', { isInteger: true }), {
      message: 'Le voyage principal est requis'
    }),
    secondaryTrip: idOnlySchema<TripLight>(createNumberSchema('id', { isInteger: true }), {
      message: 'Le voyage secondaire est requis'
    }).nullish(),
    preferredPlace: idOnlySchema<JourneyPlaceLight>(
      createNumberSchema('id', { isInteger: true }),
      { message: 'Le lieu préféré est requis' }
    ).nullish(),
    journeyTripPlace: idOnlySchema<TripPlace>(createNumberSchema('id', { isInteger: true }), {
      message: 'Le lieu de voyage est requis'
    }).optional(),
    insurances: z.array(INSURANCE_SCHEMA).optional(),
    familyDiscount: createNumberSchema('Family Discount').nullish(),
    pricing: z
      .object({
        includeApplicationFees: z.boolean().optional(),
        includeFamilyDiscount: z.boolean().optional()
      })
      .optional(),
    promoCode: createStringSchema('promoCode').nullish(),
    cseCode: createStringSchema('cseCode').nullish()
  })
  .superRefine((value, ctx) => {
    if (value.mainTrip?.hasTransport === false) {
      if (!value.preferredPlace) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['preferredPlace'],
          message: 'Le lieu de séjour est requis'
        })
      }
      return
    }

    if (!value.journeyTripPlace) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['journeyTripPlace'],
        message: 'Le lieu de voyage est requis'
      })
    }
  })

const ENROLLMENT_BASE_SCHEMA = (t: TFunction<AvailableLanguages, undefined>) =>
  CHILD_INFORMATION_SCHEMA(t)
    .merge(PARENT_INFORMATION_BASE_SCHEMA(t))
    .merge(ADDRESS_INFORMATION_SCHEMA)
    .merge(CONTACT_INFORMATION_SCHEMA(t))
    .merge(
      z.object({
        declaredFriends: z.array(FRIEND_SCHEMA).optional(),

        // z.coerce turns a null (a quote-born enrollment) into 0: force a real option id
        howDidYouHearAboutUsOptionId: createNumberSchema('Comment avez-vous connu LEC ?', {
          errorMessage: 'Veuillez indiquer comment vous avez connu LEC'
        }).min(1, { message: 'Veuillez indiquer comment vous avez connu LEC' }),
        medicalLevel: createEnumSchema({
          enumType: MedicalConditionEnum,
          fieldName: 'Medical Level'
        }),
        hasChildHealtInformation: z.boolean().default(false),
        medicalInformations: createStringSchema('Informations médicales', { nullish: true }),
        medicalInformationsEn: createStringSchema('Informations médicales', { nullish: true }),
        additionalMedicalInformations: createStringSchema('Information complémentaire', {
          nullish: true
        }),
        additionalMedicalInformationsEn: createStringSchema('Information complémentaire', {
          nullish: true
        }),
        canGoOutAlone: z.boolean().nullish()
      })
    )

export const ENROLLMENT_SCHEMA = (t: TFunction<AvailableLanguages, undefined>) =>
  ENROLLMENT_BASE_SCHEMA(t).superRefine((value, ctx) =>
    addParentContactPriorityValidation(t, value, ctx)
  )

export const CREATE_ENROLLMENT_SCHEMA = (t: TFunction<AvailableLanguages, undefined>) =>
  ENROLLMENT_BASE_SCHEMA(t)
    .merge(
      z.object({
        proposition: ENROLLMENT_PROPOSITION_SCHEMA.optional(),
        otoIndividualProposition: z
          .custom<OtoIndividualPropositionInput>((value) => !!value)
          .optional()
      })
    )
    .superRefine((value, ctx) => {
      addParentContactPriorityValidation(t, value, ctx)

      // an enrollment carries either a normal proposition or an OTO Individual one
      if (!value.proposition && !value.otoIndividualProposition)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Le séjour est requis',
          path: ['proposition']
        })
    })

export const ENROLLMENT_QUOTATION_SCHEMA = (t: TFunction<AvailableLanguages, undefined>) =>
  z
    .object({
      childFirstName: createStringSchema('Prénom'),
      childLastName: createStringSchema('NOM'),

      parent1Gender: createEnumSchema({ enumType: GenderEnum, fieldName: 'Genre' }),
      parent1FirstName: createStringSchema('Prénom', { nullish: true }),
      parent1LastName: createStringSchema('NOM'),
      parent1Email: createStringSchema('Email', { isEmail: true }),
      parent1PhoneNumber: PHONE_NUMBER_SCHEMA(t),
      declaredFriends: z.array(FRIEND_SCHEMA).optional(),
      proposition: ENROLLMENT_PROPOSITION_SCHEMA.optional(),
      otoIndividualProposition: z
        .custom<OtoIndividualPropositionInput>((value) => !!value)
        .optional()
    })
    .superRefine((value, ctx) => {
      // a quotation carries either a normal proposition or an OTO Individual one
      if (!value.proposition && !value.otoIndividualProposition)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Le séjour est requis',
          path: ['proposition']
        })
    })

type EnrollmentPropositionToPropositionReductionInput = Pick<
  EnrollmentProposition,
  'pricing' | 'promoCode'
>

export const mapEnrollmentPropositionToPropositionReductionInput = (
  proposition: EnrollmentPropositionToPropositionReductionInput,
  cseCode?: string
): PropositionReductionInput => {
  return {
    pricing: proposition.pricing
      ? {
          includeFamilyDiscount: proposition.pricing.includeFamilyDiscount,
          includeApplicationFees: proposition.pricing.includeApplicationFees
        }
      : undefined,
    promoCode: proposition.promoCode?.code,
    cseCode
  }
}

export type EnrollmentPropositionToInputData = Pick<EnrollmentProposition, 'familyArrangement'> & {
  journey?: JourneyEnrollmentPropositionInput
  insurances?: { insurance: InsuranceEnrollmentPropositionInput }[]
  options?: { option: OptionEnrollmentPropositionInput }[]
  mainTrip?: TripEnrollmentPropositionInput
  secondaryTrip?: TripEnrollmentPropositionInput | null
  preferredPlace?: JourneyPlaceEnrollmentPropositionInput | null
  journeyTripPlace?: TripPlaceEnrollmentPropositionInput
} & EnrollmentPropositionToPropositionReductionInput

export const mapEnrollmentPropositionToInput = (
  proposition: EnrollmentPropositionToInputData,
  { cseCode }: { cseCode?: string } = {}
): EnrollmentPropositionInput => {
  return {
    journey: proposition.journey,
    familyArrangement: proposition.familyArrangement,
    insurances: InsuranceProperties.mapEnrollmentInsurancesToInput(proposition.insurances),
    journeyTripPlace: proposition.journeyTripPlace ?? undefined,
    mainTrip: proposition.mainTrip,
    options: proposition.options?.map((option) => option.option),
    preferredPlace: proposition.preferredPlace,
    secondaryTrip: proposition.secondaryTrip,
    ...mapEnrollmentPropositionToPropositionReductionInput(proposition, cseCode)
  }
}

export const mapEnrollmentToEnrollmentQuotationInput = (
  enrollment: Pick<
    Enrollment,
    | 'childFirstName'
    | 'childLastName'
    | 'parent1Email'
    | 'parent1FirstName'
    | 'parent1Gender'
    | 'parent1LastName'
    | 'parent1PhoneNumber'
  > & {
    mainProposition?: EnrollmentPropositionToInputData
    otoIndividualProposition?: Enrollment['otoIndividualProposition']
    declaredFriends?: Pick<
      NonNullable<Enrollment['declaredFriends']>[number],
      'firstName' | 'lastName' | 'type'
    >[]
  },
  { cseCode }: { cseCode?: string } = {}
): EnrollmentQuotationInput => {
  return {
    parent1Email: enrollment.parent1Email,
    parent1FirstName: enrollment.parent1FirstName,
    parent1Gender: enrollment.parent1Gender,
    parent1LastName: enrollment.parent1LastName,
    parent1PhoneNumber: enrollment.parent1PhoneNumber,
    childFirstName: enrollment.childFirstName,
    childLastName: enrollment.childLastName,
    ...(enrollment.mainProposition && {
      proposition: {
        ...mapEnrollmentPropositionToInput(enrollment.mainProposition, { cseCode }),
        declaredFriends: enrollment.declaredFriends?.map((friend) => ({
          firstName: friend.firstName,
          lastName: friend.lastName,
          type: friend.type
        }))
      }
    }),
    ...(enrollment.otoIndividualProposition && {
      otoIndividualProposition: mapOtoIndividualPropositionToInput(
        enrollment.otoIndividualProposition
      )
    })
  }
}

type EnrollmentToChildInformationInput = Pick<
  Enrollment,
  | 'childBirthDate'
  | 'childEmail'
  | 'childFirstName'
  | 'childLastName'
  | 'childGender'
  | 'childNationality'
  | 'childNumberOfYearsInLanguage'
  | 'childPhoneNumber'
  | 'childSchoolLevel'
>

export const mapEnrollmentToChildInformationInput = (
  enrollment: EnrollmentToChildInformationInput
): ChildInformationInput => {
  return {
    childBirthDate: enrollment.childBirthDate,
    childEmail: enrollment.childEmail,
    childFirstName: enrollment.childFirstName,
    childGender: enrollment.childGender,
    childLastName: enrollment.childLastName,
    childNationality: enrollment.childNationality,
    childNumberOfYearsInLanguage: enrollment.childNumberOfYearsInLanguage,
    childPhoneNumber: enrollment.childPhoneNumber,
    childSchoolLevel: enrollment.childSchoolLevel
  }
}

type EnrollmentToParentInformationInput = Pick<
  Enrollment,
  | 'parent1Email'
  | 'parent1FirstName'
  | 'parent1Gender'
  | 'parent1Job'
  | 'parent1LastName'
  | 'parent1PhoneNumber'
  | 'parent1PhoneNumber2'
  | 'parent2Email'
  | 'parent2FirstName'
  | 'parent2Gender'
  | 'parent2Job'
  | 'parent2LastName'
  | 'parent2PhoneNumber'
  | 'parent2PhoneNumber2'
  | 'contactPriority'
  | 'isAA'
> & {
  cseRegistration?: Pick<NonNullable<Enrollment['cseRegistration']>, 'extraOptionLabel'> | null
}

export const mapEnrollmentToParentInformationInput = (
  enrollment: EnrollmentToParentInformationInput
): ParentInformationInput => {
  return {
    parent1Email: enrollment.parent1Email,
    parent1FirstName: enrollment.parent1FirstName,
    parent1Gender: enrollment.parent1Gender,
    parent1Job: enrollment.parent1Job,
    parent1LastName: enrollment.parent1LastName,
    parent1PhoneNumber: enrollment.parent1PhoneNumber,
    parent1PhoneNumber2: enrollment.parent1PhoneNumber2,
    parent2Email: enrollment.parent2Email,
    parent2FirstName: enrollment.parent2FirstName,
    parent2Gender: enrollment.parent2Gender,
    parent2Job: enrollment.parent2Job,
    parent2LastName: enrollment.parent2LastName,
    parent2PhoneNumber: enrollment.parent2PhoneNumber,
    parent2PhoneNumber2: enrollment.parent2PhoneNumber2,
    contactPriority: enrollment.contactPriority,
    isAA: enrollment.isAA,
    extraOptionLabel: enrollment.cseRegistration?.extraOptionLabel
  }
}

type EnrollmentToAddressInformationInput = Pick<
  Enrollment,
  'address1' | 'address2' | 'city' | 'country' | 'postalCode'
>
export const mapEnrollmentToAddressInformationInput = (
  enrollment: EnrollmentToAddressInformationInput
): AddressInformationInput => {
  return {
    address1: enrollment.address1,
    address2: enrollment.address2,
    city: enrollment.city,
    country: enrollment.country,
    postalCode: enrollment.postalCode
  }
}

type EnrollmentToContactInformationInput = Pick<
  Enrollment,
  | 'personToContactInCaseOfEmergency'
  | 'personToContactInCaseOfEmergencyFullName'
  | 'personToContactInCaseOfEmergencyPhoneNumber'
  | 'parent1FirstName'
  | 'parent1LastName'
  | 'parent1PhoneNumber'
  | 'parent2FirstName'
  | 'parent2LastName'
  | 'parent2PhoneNumber'
>
export const mapEnrollmentToContactInformationInput = (
  enrollment: EnrollmentToContactInformationInput
): ContactInformationInput => {
  return {
    personToContactInCaseOfEmergency: enrollment.personToContactInCaseOfEmergency,
    personToContactInCaseOfEmergencyFullName: enrollment.personToContactInCaseOfEmergencyFullName,
    personToContactInCaseOfEmergencyPhoneNumber:
      enrollment.personToContactInCaseOfEmergencyPhoneNumber,
    parent1FirstName: enrollment.parent1FirstName,
    parent1LastName: enrollment.parent1LastName,
    parent1PhoneNumber: enrollment.parent1PhoneNumber,
    parent2FirstName: enrollment.parent2FirstName,
    parent2LastName: enrollment.parent2LastName,
    parent2PhoneNumber: enrollment.parent2PhoneNumber
  }
}

type EnrollmentToFriendInput = {
  declaredFriends?: Pick<
    NonNullable<Enrollment['declaredFriends']>[number],
    'firstName' | 'lastName' | 'type'
  >[]
}

export const mapEnrollmentToFriendsInput = (
  enrollment: EnrollmentToFriendInput
): FriendInput[] => {
  return (
    enrollment.declaredFriends?.map((friend) => ({
      firstName: friend.firstName,
      lastName: friend.lastName,
      type: friend.type
    })) ?? []
  )
}

export const mapDeclaredFriendsToFriendInput = (
  declaredFriends?: EnrollmentInput['declaredFriends']
): FriendInput[] => {
  return (
    declaredFriends?.map((friend) => ({
      id: friend.id,
      firstName: friend.firstName,
      lastName: friend.lastName,
      type: friend.type
    })) ?? []
  )
}

type EnrollmentToEnrollmentInput = EnrollmentToChildInformationInput &
  EnrollmentToParentInformationInput &
  EnrollmentToAddressInformationInput &
  EnrollmentToContactInformationInput &
  EnrollmentToFriendInput &
  Pick<
    Enrollment,
    | 'parentAuthorizedToGoOutAlone'
    | 'howDidYouHearAboutUsOptionId'
    | 'medicalInformations'
    | 'medicalInformationsEn'
    | 'additionalMedicalInformations'
    | 'additionalMedicalInformationsEn'
    | 'canGoOutAlone'
    | 'medicalLevel'
    | 'medicalStatus'
  > & {
    cseRegistration?: Pick<NonNullable<Enrollment['cseRegistration']>, 'extraOptionLabel'> | null
  }

export const mapEnrollmentToEnrollmentInput = (
  enrollment: EnrollmentToEnrollmentInput
): EnrollmentInput => {
  return {
    ...mapEnrollmentToChildInformationInput(enrollment),
    ...mapEnrollmentToParentInformationInput(enrollment),
    ...mapEnrollmentToAddressInformationInput(enrollment),
    ...mapEnrollmentToContactInformationInput(enrollment),
    declaredFriends: mapEnrollmentToFriendsInput(enrollment),
    parentAuthorizedToGoOutAlone: enrollment.parentAuthorizedToGoOutAlone,
    howDidYouHearAboutUsOptionId: enrollment.howDidYouHearAboutUsOptionId,
    medicalInformations: enrollment.medicalInformations,
    medicalInformationsEn: enrollment.medicalInformationsEn,
    additionalMedicalInformations: enrollment.additionalMedicalInformations,
    canGoOutAlone: enrollment.canGoOutAlone,

    medicalLevel: enrollment.medicalLevel,
    additionalMedicalInformationsEn: enrollment.additionalMedicalInformationsEn,
    hasChildHealtInformation: enrollment.medicalLevel !== MedicalConditionEnum.RAS,
    medicalStatus: enrollment.medicalStatus,
    extraOptionLabel: enrollment.cseRegistration?.extraOptionLabel
  }
}

export type EnrollmentCreationStateInput = Pick<Enrollment, 'id'> &
  EnrollmentToEnrollmentInput & {
    cseRegistration?: {
      cse: Pick<NonNullable<Enrollment['cseRegistration']>['cse'], 'code'>
    } | null
    mainProposition?: EnrollmentPropositionToInputData
    otoIndividualProposition?: Enrollment['otoIndividualProposition']
  }

export const mapEnrollmentToCreateEnrollmentInput = (
  enrollment: EnrollmentCreationStateInput,
  { cseCode }: { cseCode?: string } = {}
): CreateEnrollmentInput => {
  return {
    ...mapEnrollmentToEnrollmentInput(enrollment),
    ...(enrollment.mainProposition && {
      proposition: mapEnrollmentPropositionToInput(enrollment.mainProposition, { cseCode })
    }),
    ...(enrollment.otoIndividualProposition && {
      otoIndividualProposition: mapOtoIndividualPropositionToInput(
        enrollment.otoIndividualProposition
      )
    })
  }
}

export const mapEnrollmentPropositionInputToRequest = (
  input: EnrollmentPropositionInput,
  withoutAdditionalFields = false
): EnrollmentPropositionRequest => {
  if (!input.journey) throw new Error('Missing required fields in the input')

  return {
    cseCode: input.cseCode,
    journeyId: input.journey.id,
    familyArrangement: input.familyArrangement,
    journeyTripPlaceId:
      input.journeyTripPlace !== undefined ? (input.journeyTripPlace?.id ?? null) : undefined,
    mainTripId: input.mainTrip?.id,
    secondaryTripId:
      input.secondaryTrip !== undefined ? (input.secondaryTrip?.id ?? null) : undefined,
    preferredPlaceId:
      input.preferredPlace !== undefined ? (input.preferredPlace?.id ?? null) : undefined,
    optionIds: input.options?.map((option) => option.id),
    ...(!withoutAdditionalFields && {
      insuranceIds: input.insurances?.map((insurance) => insurance.id),
      pricing: input.pricing,
      promoCode: input.promoCode
    })
  }
}

export const mapEnrollmentPropositionInputToUpdateRequest = (
  input: Partial<EnrollmentPropositionInput>
): Partial<EnrollmentPropositionRequest> => {
  return {
    journeyId: input.journey?.id,
    familyArrangement: input.familyArrangement,
    journeyTripPlaceId:
      input.journeyTripPlace !== undefined ? (input.journeyTripPlace?.id ?? null) : undefined,
    mainTripId: input.mainTrip?.id,
    secondaryTripId: input.secondaryTrip?.id,
    preferredPlaceId: input.preferredPlace?.id,
    optionIds: input.options?.map((option) => option.id),
    insuranceIds: input.insurances?.map((insurance) => insurance.id),
    pricing: input.pricing,
    promoCode: input.promoCode
  }
}

export const getParentEmailBasedOnPriority = (
  enrollment: Pick<Enrollment, 'parent1Email' | 'parent2Email' | 'contactPriority'>
) => {
  if (enrollment.contactPriority === ContactEnum.PARENT_2) {
    return enrollment.parent2Email
  }
  return enrollment.parent1Email
}
